/**
 * Toast stack (top on phones, top right over the side column on larger
 * screens, clear of the Play button).
 * Auto-dismiss pauses while hovered or focused; sticky toasts (duration 0)
 * wait for their action or the close button. The stack is not a live region:
 * uiStore.toast() announces each message through <LiveAnnouncer/>.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useUiStore, type Toast } from '../state/uiStore';
import { IconCheck, IconClose, IconRefresh, IconSparkle, IconX } from './Icons';
import './Toasts.css';

function ToastIcon({ kind }: { kind: Toast['kind'] }) {
  if (kind === 'success') return <IconCheck />;
  if (kind === 'error') return <IconX />;
  if (kind === 'warning') return <IconRefresh />;
  return <IconSparkle />;
}

/** Reduced motion: a plain fade of ≤ 120 ms, no slide, scale or layout glide. */
const REDUCED_FADE = { duration: 0.12 } as const;

function ToastItem({ toast, reduced }: { toast: Toast; reduced: boolean }) {
  const dismiss = useUiStore((s) => s.dismissToast);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (toast.duration === 0 || paused) return;
    const timer = setTimeout(() => dismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast, paused, dismiss]);

  return (
    <motion.div
      layout={!reduced}
      className={clsx('toast', `toast--${toast.kind}`)}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: -14, scale: 0.96 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
      exit={reduced ? { opacity: 0, transition: REDUCED_FADE } : { opacity: 0, scale: 0.96, transition: { duration: 0.16 } }}
      transition={reduced ? REDUCED_FADE : { type: 'spring', stiffness: 460, damping: 34 }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast__icon" aria-hidden="true">
        <ToastIcon kind={toast.kind} />
      </span>
      <span className="toast__message">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className="btn btn--sm btn--primary toast__action"
          onClick={() => {
            toast.action?.run();
            dismiss(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="toast__close" aria-label="Dismiss notification" onClick={() => dismiss(toast.id)}>
        <IconClose />
      </button>
    </motion.div>
  );
}

export function Toasts() {
  const toasts = useUiStore((s) => s.toasts);
  const reduced = useReducedMotion();
  return createPortal(
    <section className="toasts" aria-label="Notifications">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} reduced={reduced} />
        ))}
      </AnimatePresence>
    </section>,
    document.body,
  );
}
